import { discountMatchesEnabledSubject } from './promoCode';

let discountDataCache = {};
// Bumped on every data update — lets consumers (e.g. the per-option price cache in pricing.js)
// invalidate anything derived from discount data without subscribing to it.
let discountDataVersion = 0;

export const setAutomaticDiscountData = data => {
  discountDataCache = data || {};
  discountDataVersion += 1;
};

export const getAutomaticDiscountData = () => discountDataCache;

export const getAutomaticDiscountDataVersion = () => discountDataVersion;

/**
 * Resolves the best matching automatic discount for a week.
 *
 * Mirrors YachtListManager::resolveDiscountForSubjectAndPackage() on the backend and the campaign
 * restrictions enforced in PromotionalCodeManager::findValidPromoCodeOnBookingFromCode(). Keep the
 * three in sync: a discount that the UI advertises but the booking rejects is worse than no badge.
 *
 * @param {Object} discountData    Week keyed discount data
 * @param {number} weekId          Week ID
 * @param {string} subject         Booking subject ('yacht' | 'cabin' | 'solo')
 * @param {number|null} [packageId] Package ID, or null when no package applies (BYO / not yet chosen)
 * @param {boolean} [isNonStandard] True for BYO (custom yacht) options, which are gated on the
 *                                 campaign's "active on non standard bookings" flag instead of packages
 * @returns The best matching discount, or null when none reaches this option
 */
export const resolveDiscountForSubjectAndPackage = (discountData, weekId, subject, packageId, isNonStandard = false) => {
  const weekData = discountData[weekId];
  if (!weekData) {
    return null;
  }

  const discounts = weekData.discounts;
  if (!discounts || !Array.isArray(discounts)) {
    return null;
  }

  let best = null;
  let bestPercentage = 0;

  for (const d of discounts) {
    const subjects = d.enabledBookingSubjects ?? [];
    if (!discountMatchesEnabledSubject(subjects, subject)) {
      continue;
    }

    if (isNonStandard) {
      // BYO yachts carry no package, so package restrictions never apply to them - the campaign is
      // either enabled on non standard bookings or it does not reach them at all.
      if (!d.isActiveOnNonStandardBookings) {
        continue;
      }
    } else {
      const enabledPackages = d.enabledPackages ?? [];
      if (packageId != null && enabledPackages.length > 0 && !enabledPackages.includes(Number(packageId))) {
        continue;
      }
    }

    const pct = Number(d.discountPercentage ?? 0);
    if (pct > bestPercentage) {
      bestPercentage = pct;
      best = d;
    }
  }

  return best;
};

export const getDiscountForWeekAndSubjectFromCache = (weekId, subject, packageId, isNonStandard = false) =>
  resolveDiscountForSubjectAndPackage(discountDataCache, weekId, subject, packageId, isNonStandard);
